import React from 'react'
import { Checkbox, Form, Spin, Switch } from 'antd'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import { shopStore } from '@/store/shop'
import $msg from '@/utils/message'
import Auth from '@/views/main/components/auth/auth'
import { getShopInfo, loadMemberSettingConfig, saveMemberSettingConfig } from '../api'
import SettingPage, {
    SCENE_OPTIONS,
    SettingActions,
    SettingGrid,
    SettingInputNumber,
    SettingPanel,
    SettingPreview,
    boolToStatus,
    numberOrUndefined,
    statusToBool,
} from './setting-page'
import { validateChainCoinDeductionValues } from './chain-coin-setting-utils'

interface ChainCoinForm {
    status: boolean
    coin_money?: number
    percentage_ceiling?: number
    mall_percentage_ceiling?: number
    scene_list?: number[]
}

const ChainCoinSetting: React.FC = () => {
    const [form] = Form.useForm<ChainCoinForm>()
    const loadReq = useRequest(
        async () => {
            const [config, shopInfo] = await Promise.all([
                loadMemberSettingConfig('/chain/getchaincoindeductionconfig'),
                getShopInfo(),
            ])
            const chainCoinConfig = shopInfo?.chain_shop
                ? await loadMemberSettingConfig('/chain/getchaincoinconfigbranch')
                : null

            return {
                config,
                chainCoinConfig,
                isChainShop: !!shopInfo?.chain_shop,
                shopInfo,
            }
        },
        {
            onSuccess: res => {
                shopStore.setShopInfo(res.shopInfo)
                form.setFieldsValue({
                    status: statusToBool(res?.config?.status),
                    coin_money: Number(res?.config?.coin_money || 1),
                    percentage_ceiling: Number(res?.config?.percentage_ceiling || 0),
                    mall_percentage_ceiling: Number(res?.config?.mall_percentage_ceiling || 0),
                    scene_list: res?.config?.scene_list || [],
                })
            },
            onError: e => $msg('error', e.message || '获取连锁币设置失败'),
        },
    )
    const saveReq = useRequest(
        async (values: ChainCoinForm) =>
            saveMemberSettingConfig('/chain/updatechaincoindeduction', {
                status: boolToStatus(values.status),
                coin_money: numberOrUndefined(values.coin_money),
                percentage_ceiling: numberOrUndefined(values.percentage_ceiling),
                mall_percentage_ceiling: numberOrUndefined(values.mall_percentage_ceiling),
                scene_list: values.scene_list || [],
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '保存成功')
                loadReq.refresh()
            },
            onError: e => $msg('error', e.message || '保存连锁币设置失败'),
        },
    )

    const onSubmit = async () => {
        const values = await form.validateFields()
        if (!loadReq.data?.isChainShop) {
            $msg('warning', '当前店铺不是连锁店，暂不支持配置连锁币')

            return
        }
        const chainCoinDeductionError = validateChainCoinDeductionValues(
            values,
            statusToBool(loadReq.data?.chainCoinConfig?.status),
        )
        if (chainCoinDeductionError) {
            $msg('warning', chainCoinDeductionError)

            return
        }
        saveReq.run(values)
    }

    return (
        <SettingPage title="连锁币">
            <Spin spinning={loadReq.loading}>
                {!loadReq.data ? null : !loadReq.data.isChainShop ? (
                    <SettingGrid>
                        <SettingPanel title="连锁币不可用" desc="连锁币仅支持连锁总店或连锁分店配置。" wide>
                            <div className="member-config-page__notice">
                                当前店铺不是连锁店，暂不支持配置连锁币抵现规则。
                            </div>
                        </SettingPanel>
                    </SettingGrid>
                ) : (
                    <Form form={form} layout="vertical">
                        <SettingGrid>
                            <SettingPanel title="抵现规则" desc="配置本店是否允许会员使用连锁币抵现，以及抵现上限。">
                                <Form.Item name="status" label="抵现状态" valuePropName="checked">
                                    <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                                </Form.Item>
                                <Form.Item name="scene_list" label="使用场景">
                                    <Checkbox.Group options={SCENE_OPTIONS} />
                                </Form.Item>
                                <Form.Item name="coin_money" label="抵现比例">
                                    <SettingInputNumber
                                        min={1}
                                        precision={0}
                                        addonBefore="每"
                                        addonAfter="连锁币抵 1 元"
                                    />
                                </Form.Item>
                                <Form.Item name="percentage_ceiling" label="收银台单笔抵扣上限">
                                    <SettingInputNumber min={0} max={1} precision={4} addonAfter="订单比例" />
                                </Form.Item>
                                <Form.Item name="mall_percentage_ceiling" label="商城单笔抵扣上限">
                                    <SettingInputNumber min={0} max={1} precision={4} addonAfter="订单比例" />
                                </Form.Item>
                            </SettingPanel>
                            <SettingPanel title="使用效果" desc="根据当前配置预览会员实际使用连锁币时的规则。">
                                <Form.Item noStyle shouldUpdate>
                                    {() => (
                                        <SettingPreview
                                            items={[
                                                {
                                                    label: '抵现换算',
                                                    value: `${form.getFieldValue('coin_money') || '--'} 连锁币 = 1 元`,
                                                },
                                                {
                                                    label: '收银台上限',
                                                    value: `${
                                                        Number(form.getFieldValue('percentage_ceiling') || 0) * 100
                                                    }%`,
                                                },
                                                {
                                                    label: '商城上限',
                                                    value: `${
                                                        Number(form.getFieldValue('mall_percentage_ceiling') || 0) * 100
                                                    }%`,
                                                },
                                            ]}
                                        />
                                    )}
                                </Form.Item>
                                <div className="member-config-page__notice">
                                    抵扣上限表示每笔订单最多可使用连锁币抵扣的订单金额比例。
                                </div>
                                <SettingActions>
                                    <Auth perssion={20} onClick={onSubmit}>
                                        <Button types="default" loading={saveReq.loading} disabled={saveReq.loading}>
                                            保存
                                        </Button>
                                    </Auth>
                                </SettingActions>
                            </SettingPanel>
                        </SettingGrid>
                    </Form>
                )}
            </Spin>
        </SettingPage>
    )
}

export default ChainCoinSetting
